import React, { useEffect, useState, useRef } from 'react'
import './Resources.css'
import TeamHeader from "../TeamHeader/TeamHeader";
import FilterDropdown from "../TeamPortalUtil/FilterDropdown";

// Icons
import { LuDownload } from "react-icons/lu";
import { RiNotionFill } from "react-icons/ri";
import { TfiPrinter } from "react-icons/tfi";
import { GrDocumentPdf } from "react-icons/gr";

// Interface
export interface MarketingAsset {
    id: string;
    title: string;
    description: string;
    category: string;
    industry: string;
    url: string;
    digitalUrl: string;
    digitalName: string;
    printUrl: string;
    printName: string;
}


const Resources = () => {

    // Variables
    const [assets, setAssets] = useState<MarketingAsset[]>([])
    const [loadingWhitePapers, setLoadingWhitePapers] = useState(true);

    const [externalFeatures, setExternalFeatures] = useState<MarketingAsset[]>([])
    const [loadingExternalFeatures, setLoadingExternalFeatures] = useState(true);

    const [awardsAndRecognitions, setAwardsAndRecognitions] = useState<MarketingAsset[]>([])
    const [loadingAwardsAndRecognitions, setLoadingAwardsAndRecognitions] = useState(true);

    const [researchPublications, setResearchPublications] = useState<MarketingAsset[]>([])
    const [loadingResearchPublications, setLoadingResearchPublications] = useState(true);

    // -------- Industry Filter --------
    const availableIndustries = ["Utility", "Wildfire", "Other"];
    const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);

    // Fetching all Notion endpoints
    const fetchNotionAssets = async (
        url: string,
        setData: React.Dispatch<React.SetStateAction<MarketingAsset[]>>,
        setLoading: React.Dispatch<React.SetStateAction<boolean>>,
        label = "assets"
    ) => {
        try {
            setLoading(true);

            const response = await fetch(url, { credentials: "same-origin" });
            const data = await response.json();

            if (!response.ok) {
                console.error(`Failed to load ${label}:`, data?.error || response.statusText);
                return;
            }

            const dbBlock = data.blocks?.find((block: any) => block.type === "child_database");

            if (!dbBlock?.rows) {
                console.warn(`No child database found in Notion response for ${label}`);
                return;
            }

            const formattedRows: MarketingAsset[] = dbBlock.rows.map((row: any) => {
                const props = row.properties;

                const getFileUrl = (field: string) =>
                    props[field]?.files?.[0]?.file?.url
                    || props[field]?.files?.[0]?.external?.url
                    || "";

                const getFileName = (field: string) =>
                    props[field]?.files?.[0]?.name
                    || props[field]?.files?.[0]?.external?.url
                    || "";

                return {
                    id: row.id,
                    title: props.Title?.title?.[0]?.plain_text || "",
                    description: props.Description?.rich_text?.[0]?.plain_text || "",
                    category: props.Category?.select?.name || "",
                    industry: props.Industry?.select?.name || "",
                    url: props.URL?.url || "",
                    digitalUrl: getFileUrl("Digital Version"),
                    digitalName: getFileName("Digital Version"),
                    printUrl: getFileUrl("Print Version"),
                    printName: getFileName("Print Version"),
                };
            });

            setData(formattedRows);
        } catch (error) {
            console.error(`❌ Error fetching ${label}:`, error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotionAssets(
            "/api/notion/block-children/28c3634fc8d98066b728ca624dba46e5/",
            setAssets,
            setLoadingWhitePapers,
            "White Papers"
        );

        fetchNotionAssets(
            "/api/notion/block-children/2fe3634fc8d980038285ecd3c4ba269b/",
            setExternalFeatures,
            setLoadingExternalFeatures,
            "External Features"
        );

        fetchNotionAssets(
            "/api/notion/block-children/2813634fc8d980b59d17fcb7ec911602/",
            setAwardsAndRecognitions,
            setLoadingAwardsAndRecognitions,
            "Awards & Recognitions"
        );

        fetchNotionAssets(
            "/api/notion/block-children/3033634fc8d9802aabccd3d471a4997d/",
            setResearchPublications,
            setLoadingResearchPublications,
            "Research Publications"
        );
    }, []);

    /* ----------------------------
        Reusable Widget
    -----------------------------*/
    const MarketingAssetWidget = ({ asset }: { asset: MarketingAsset }) => {
        const INDUSTRY_COLORS: Record<string, string> = {
            Utility: "#3F6FD8",
            Wildfire: "#FF6600",
            Other: "#8A94A6",
        };

        return (
            <div className='marketing-resource-widget'>
                <div>
                    <div
                        className='marketing-resource-industry'
                        style={{ backgroundColor: INDUSTRY_COLORS[asset.industry] || "#8A94A6" }}
                    >
                        {asset.industry}
                    </div>
                    <h2>{asset.title}</h2>
                    <p>{asset.description}</p>
                </div>
                <div className='marketing-resource-buttons'>
                    {asset.digitalUrl && (
                        <a href={asset.digitalUrl} target="_blank" rel="noreferrer">
                            <button className='confirm-btn mailjet-confirm'>
                                <GrDocumentPdf /> Digital Version
                            </button>
                        </a>
                    )}
                    {asset.printUrl && (
                        <a href={asset.printUrl} target="_blank" rel="noreferrer">
                            <button className='confirm-btn mailjet-confirm'>
                                <TfiPrinter /> Print Version
                            </button>
                        </a>
                    )}
                </div>
            </div>
        );
    };

    /* ----------------------------
        Filter Logic
    -----------------------------*/
    // White Papers (filtered by industry only)
    const filteredWhitePapers = assets.filter(asset =>
        selectedIndustries.length === 0 || selectedIndustries.includes(asset.industry)
    );

    // External Features (filtered by industry only)
    const filteredExternalFeatures = externalFeatures.filter(asset =>
        selectedIndustries.length === 0 || selectedIndustries.includes(asset.industry)
    );

    // Awards & Recognitions (filtered by industry only)
    const filteredAwardsAndRecognitions = awardsAndRecognitions.filter(asset =>
        selectedIndustries.length === 0 || selectedIndustries.includes(asset.industry)
    );

    // ResearchPublications (filtered by industry only)
    const filteredResearchPublications = researchPublications.filter(asset =>
        selectedIndustries.length === 0 || selectedIndustries.includes(asset.industry)
    );


    return (
        <div className="customer-directory-body">
            <TeamHeader title="Marketing" />

            <div className="customer-title-and-expand-buttons">
                <h1 className="cutomer-title with-less-margin">Resources</h1>
            </div>

            {/* FilterDropdown */}
            <FilterDropdown
                dropdownClassName="filter-dropdown"
                sections={[
                    {
                        key: "industries",
                        title: "Industry",
                        options: availableIndustries,
                        type: "checkbox",
                    },
                ]}
                onChange={(filters) => {
                    setSelectedIndustries(filters.industries || []);
                }}
            />


            <div className='marketing-resources-wrapper with-more-margin'>

                { /* White Papers */}
                <div className='marketing-resources-group'>

                    <div className='marketing-resources-group-title-and-notion'>
                        <h2>White Papers </h2>
                        <a href="https://www.notion.so/Print-Digital-Material-28c3634fc8d98066b728ca624dba46e5" target="_blank" rel="noreferrer">
                            <button>
                                <RiNotionFill />
                            </button>
                        </a>
                    </div>

                    <div className='marketing-resources-widgets'>

                        {loadingWhitePapers
                            ? Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="marketing-resource-widget">
                                    <div>
                                        <div className="marketing-skeleton marketing-skeleton-tag" />
                                        <div className="marketing-skeleton marketing-skeleton-title" />
                                        <div className="marketing-skeleton marketing-skeleton-text" />
                                        <div className="marketing-skeleton marketing-skeleton-text short" />
                                    </div>
                                    <div className="marketing-skeleton marketing-skeleton-button" />
                                </div>
                            ))
                            : filteredWhitePapers.map(asset => (
                                <MarketingAssetWidget key={asset.id} asset={asset} />
                            ))
                        }


                    </div>

                </div>

                { /* External Features */}
                <div className='marketing-resources-group'>

                    <div className='marketing-resources-group-title-and-notion'>
                        <h2>External Features  </h2>
                        <a href="https://www.notion.so/External-Features-2fe3634fc8d980038285ecd3c4ba269b" target="_blank" rel="noreferrer">
                            <button>
                                <RiNotionFill />
                            </button>
                        </a>
                    </div>


                    <div className='marketing-resources-widgets'>

                        {loadingExternalFeatures
                            ? Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="marketing-resource-widget">
                                    <div>
                                        <div className="marketing-skeleton marketing-skeleton-tag" />
                                        <div className="marketing-skeleton marketing-skeleton-title" />
                                        <div className="marketing-skeleton marketing-skeleton-text" />
                                        <div className="marketing-skeleton marketing-skeleton-text short" />
                                    </div>
                                    <div className="marketing-skeleton marketing-skeleton-button" />
                                </div>
                            ))
                            : filteredExternalFeatures.map(asset => (
                                <MarketingAssetWidget key={asset.id} asset={asset} />
                            ))
                        }


                    </div>

                </div>

                { /* Awards & Recognitions */}
                <div className='marketing-resources-group'>

                    <div className='marketing-resources-group-title-and-notion'>
                        <h2>Awards & Recognitions </h2>
                        <a href="https://www.notion.so/Awards-Recognitions-2813634fc8d980b59d17fcb7ec911602" target="_blank" rel="noreferrer">
                            <button>
                                <RiNotionFill />
                            </button>
                        </a>
                    </div>


                    <div className='marketing-resources-widgets'>

                        {loadingAwardsAndRecognitions
                            ? Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="marketing-resource-widget">
                                    <div>
                                        <div className="marketing-skeleton marketing-skeleton-tag" />
                                        <div className="marketing-skeleton marketing-skeleton-title" />
                                        <div className="marketing-skeleton marketing-skeleton-text" />
                                        <div className="marketing-skeleton marketing-skeleton-text short" />
                                    </div>
                                    <div className="marketing-skeleton marketing-skeleton-button" />
                                </div>
                            ))
                            : filteredAwardsAndRecognitions.map(asset => (
                                <MarketingAssetWidget key={asset.id} asset={asset} />
                            ))
                        }


                    </div>

                </div>

                { /* Research Publications */}
                <div className='marketing-resources-group'>

                    <div className='marketing-resources-group-title-and-notion'>
                        <h2>Research Publications </h2>
                        <a href="https://www.notion.so/Research-Publications-3033634fc8d9802aabccd3d471a4997d" target="_blank" rel="noreferrer">
                            <button>
                                <RiNotionFill />
                            </button>
                        </a>
                    </div>


                    <div className='marketing-resources-widgets'>

                        {loadingResearchPublications
                            ? Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="marketing-resource-widget">
                                    <div>
                                        <div className="marketing-skeleton marketing-skeleton-tag" />
                                        <div className="marketing-skeleton marketing-skeleton-title" />
                                        <div className="marketing-skeleton marketing-skeleton-text" />
                                        <div className="marketing-skeleton marketing-skeleton-text short" />
                                    </div>
                                    <div className="marketing-skeleton marketing-skeleton-button" />
                                </div>
                            ))
                            : filteredResearchPublications.map(asset => (
                                <MarketingAssetWidget key={asset.id} asset={asset} />
                            ))
                        }


                    </div>

                </div>

            </div>

        </div>
    )
}

export default Resources